import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveAstroApplication,
  getAstroApplication,
  rejectAstroApplication,
  resolveMediaUrl,
} from '../../api';
import { PageHeader, StatusBadge } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import ImageViewer from '../../components/ui/ImageViewer';
import { ArrowLeft, CheckCircle, XCircle, FileText, Copy, Download, Eye, UserCheck, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

function InfoCell({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm text-gray-900 break-words">{value || '—'}</div>
    </div>
  );
}

export default function AstrologerApplicationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [approveForm, setApproveForm] = useState({
    chatPrice: 30,
    callPrice: 50,
    videoPrice: 70,
    password: '',
    note: '',
  });
  const [credentials, setCredentials] = useState<{
    loginId: string;
    password: string;
    email?: string;
    phone?: string;
    shareMessage?: string;
  } | null>(null);
  const [viewer, setViewer] = useState<{ src: string; alt: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-astro-application', id],
    queryFn: async () => {
      const r = await getAstroApplication(id!);
      return r.data?.data?.application || r.data?.application || r.data?.data;
    },
    enabled: !!id,
  });

  const app = data;

  const approveMut = useMutation({
    mutationFn: () => approveAstroApplication(id!, {
      chatPrice: approveForm.chatPrice,
      callPrice: approveForm.callPrice,
      videoPrice: approveForm.videoPrice,
      password: approveForm.password,
      note: approveForm.note,
    }),
    onSuccess: (res) => {
      const data = res.data?.data;
      const creds = data?.credentials;
      const delivery = data?.delivery;
      if (creds) {
        setCredentials({
          loginId: creds.loginId,
          password: creds.password || (creds.usedApplicantPassword
            ? '(applicant’s own password — not shown)'
            : ''),
          email: creds.email || app?.email,
          phone: creds.phone || app?.phone,
          shareMessage: delivery?.shareMessage,
        });
      }
      toast.success(
        creds?.usedApplicantPassword
          ? 'Approved — applicant keeps their chosen password'
          : 'Application approved',
      );
      setApproveOpen(false);
      qc.invalidateQueries({ queryKey: ['admin-astro-application', id] });
      qc.invalidateQueries({ queryKey: ['admin-astro-applications'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Approve failed'),
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectAstroApplication(id!, { reason }),
    onSuccess: () => {
      toast.success('Application rejected');
      setRejectOpen(false);
      setReason('');
      qc.invalidateQueries({ queryKey: ['admin-astro-application', id] });
      qc.invalidateQueries({ queryKey: ['admin-astro-applications'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Reject failed'),
  });

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  if (isLoading) {
    return <div className="p-6 text-gray-500">Loading application…</div>;
  }
  if (!app) {
    return (
      <div className="p-6">
        <p className="text-gray-500 mb-3">Application not found.</p>
        <Link to="/astrologers/applications" className="text-primary-600 text-sm">← Back</Link>
      </div>
    );
  }

  const statusKey = app.status === 'under_review' ? 'pending' : app.status;
  const profileSrc = app.profileImage ? resolveMediaUrl(app.profileImage) : '';
  const linkedUser = app.linkedUser || (app.userId && typeof app.userId === 'object' ? app.userId : null);
  const isExistingMember = !!(app.isExistingMember || linkedUser?._id);
  const memberName = linkedUser?.name || app.name || 'User';
  const memberPhone = linkedUser?.phone
    ? `${app.countryCode || '+91'} ${linkedUser.phone}`
    : `${app.countryCode || '+91'} ${app.phone}`;
  const memberId = linkedUser?._id ? String(linkedUser._id) : '';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate('/astrologers/applications')} className="hover:text-primary-600 flex items-center gap-1">
          <ArrowLeft size={14} /> Applications
        </button>
      </div>

      <PageHeader
        title={app.name}
        subtitle={`${app.applicationId} · ${app.phone}`}
      >
          <div className="flex gap-2">
            {app.status === 'under_review' && (
              <>
                <button className="btn-danger" onClick={() => setRejectOpen(true)}>
                  <XCircle size={14} /> Reject
                </button>
                <button className="btn-primary" onClick={() => setApproveOpen(true)}>
                  <CheckCircle size={14} /> Approve
                </button>
              </>
            )}
            {app.status === 'rejected' && (
              <button className="btn-primary" onClick={() => setApproveOpen(true)}>
                <CheckCircle size={14} /> Approve anyway
              </button>
            )}
            {app.astrologerId && (
              <Link
                className="btn-secondary"
                to={`/astrologers/${typeof app.astrologerId === 'object' ? app.astrologerId._id : app.astrologerId}`}
              >
                View Astrologer
              </Link>
            )}
          </div>
      </PageHeader>

      {isExistingMember && memberId && (
        <button
          type="button"
          onClick={() => navigate(`/users/${memberId}`)}
          className="w-full text-left card p-4 border border-sky-200 bg-sky-50 hover:bg-sky-100/80 transition flex flex-wrap items-center gap-4 cursor-pointer group"
        >
          <div className="w-11 h-11 rounded-xl bg-sky-600 text-white flex items-center justify-center shrink-0">
            <UserCheck size={22} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-sky-600 text-white">
                Existing member
              </span>
              <span className="text-xs text-sky-700">Already has a user account on AstroVrinda</span>
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {memberName}
              <span className="font-normal text-gray-500"> · {memberPhone}</span>
            </div>
            {linkedUser?.email && (
              <div className="text-xs text-gray-500 mt-0.5">{linkedUser.email}</div>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 group-hover:text-sky-900">
            Open user details <ExternalLink size={14} />
          </span>
        </button>
      )}

      <div className="card p-5 flex flex-wrap items-center gap-4">
        {profileSrc ? (
          <button
            type="button"
            className="relative group rounded-2xl overflow-hidden border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            onClick={() => setViewer({ src: profileSrc, alt: `${app.name} profile` })}
            title="View profile photo"
          >
            <img src={profileSrc} alt="" className="w-24 h-24 object-cover" />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition flex items-center justify-center">
              <Eye size={18} className="text-white opacity-0 group-hover:opacity-100" />
            </span>
          </button>
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-2xl">
            {String(app.name || '?')[0]}
          </div>
        )}
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold text-gray-900 text-lg">{app.name}</div>
          <div className="text-sm text-gray-500">{app.email || 'No email'}</div>
          {profileSrc && (
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
              onClick={() => setViewer({ src: profileSrc, alt: `${app.name} profile` })}
            >
              <Eye size={12} /> View profile photo
            </button>
          )}
        </div>
        <StatusBadge status={statusKey} />
        {app.assignedLoginId && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
            <div>
              Astrologer ID:{' '}
              <span className="font-mono font-semibold text-base">{app.assignedLoginId}</span>
            </div>
            {app.hasChosenPassword ? (
              <div className="text-xs text-amber-800">Applicant already chose their login password.</div>
            ) : null}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">Personal information</h3>
          <div className="grid grid-cols-2 gap-4">
            <InfoCell label="Phone" value={`${app.countryCode || '+91'} ${app.phone}`} />
            <InfoCell label="Gender" value={app.gender} />
            <InfoCell label="DOB" value={app.dateOfBirth ? new Date(app.dateOfBirth).toLocaleDateString() : undefined} />
            <InfoCell label="City" value={app.city} />
            <InfoCell label="State" value={app.state} />
            <InfoCell label="Pincode" value={app.pincode} />
            <InfoCell label="Address" value={app.address} />
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">Professional information</h3>
          <div className="grid grid-cols-2 gap-4">
            <InfoCell label="Experience" value={`${app.experience} years`} />
            <InfoCell label="Languages" value={(app.languages || []).join(', ')} />
            <InfoCell label="Specialities" value={(app.specialities || []).join(', ')} />
            <InfoCell label="Qualifications" value={(app.qualifications || []).join(', ') || '—'} />
            <InfoCell
              label="Preferred rates (₹/min)"
              value={`Chat ${app.preferredChatPrice ?? '—'} · Audio ${app.preferredCallPrice ?? '—'} · Video ${app.preferredVideoPrice ?? '—'}`}
            />
          </div>
          {app.bio && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Bio</div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.bio}</p>
            </div>
          )}
        </div>
      </div>

      <div className="card p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Photos & documents</h3>
        {(!profileSrc && !(app.documents || []).length) ? (
          <p className="text-sm text-gray-500">No photos or documents uploaded.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {profileSrc && (
              <button
                type="button"
                className="border border-gray-200 rounded-lg p-3 flex gap-3 items-center text-left hover:border-primary-300 hover:bg-orange-50/40 transition"
                onClick={() => setViewer({ src: profileSrc, alt: 'Profile photo' })}
              >
                <img src={profileSrc} alt="" className="w-16 h-16 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Profile photo</div>
                  <div className="text-xs text-primary-600 font-semibold mt-0.5 inline-flex items-center gap-1">
                    <Eye size={12} /> View full size
                  </div>
                </div>
              </button>
            )}
            {(app.documents || []).map((doc: any, i: number) => {
              const url = resolveMediaUrl(doc.url);
              const isImage = /\.(jpe?g|png|webp|gif)(\?|$)/i.test(doc.url || '') || /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);
              return (
                <div key={i} className="border border-gray-200 rounded-lg p-3 flex gap-3 items-center">
                  {isImage ? (
                    <button type="button" onClick={() => setViewer({ src: url, alt: doc.type || 'Document' })} className="shrink-0">
                      <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    </button>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                      <FileText size={22} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate capitalize">{String(doc.type || 'document').replace(/_/g, ' ')}</div>
                    <div className="text-xs text-gray-400 truncate">{doc.name || doc.url}</div>
                    {isImage && (
                      <button
                        type="button"
                        className="text-xs text-primary-600 font-semibold mt-0.5 inline-flex items-center gap-1"
                        onClick={() => setViewer({ src: url, alt: doc.type || 'Document' })}
                      >
                        <Eye size={12} /> View
                      </button>
                    )}
                  </div>
                  <a href={url} target="_blank" rel="noreferrer" className="p-2 hover:bg-gray-100 rounded text-gray-600" title="Open">
                    <Download size={16} />
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {app.rejectionReason && (
        <div className="card p-4 border border-red-200 bg-red-50 text-sm text-red-800">
          <strong>Rejection reason:</strong> {app.rejectionReason}
        </div>
      )}

      <ImageViewer
        open={!!viewer}
        src={viewer?.src || ''}
        alt={viewer?.alt}
        onClose={() => setViewer(null)}
      />

      <div className="card p-5">
        <h3 className="font-semibold text-gray-900 mb-3">History</h3>
        <ul className="space-y-3">
          {(app.history || []).slice().reverse().map((h: any, i: number) => (
            <li key={i} className="flex gap-3 text-sm">
              <StatusBadge status={h.status === 'under_review' ? 'pending' : h.status} />
              <div>
                <div className="text-gray-800">{h.note || h.status}</div>
                <div className="text-xs text-gray-400">{h.at ? new Date(h.at).toLocaleString() : ''}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Modal open={approveOpen} onClose={() => setApproveOpen(false)} title="Approve application" size="md">
        <div className="space-y-5">
          {isExistingMember && memberId && (
            <button
              type="button"
              onClick={() => {
                setApproveOpen(false);
                navigate(`/users/${memberId}`);
              }}
              className="w-full text-left rounded-xl border border-sky-200 bg-sky-50 hover:bg-sky-100 px-4 py-3 transition"
            >
              <div className="flex items-center gap-2 mb-1">
                <UserCheck size={16} className="text-sky-700" />
                <span className="text-[11px] font-bold uppercase tracking-wide text-sky-700">Existing member</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {memberName}
                <span className="font-normal text-gray-500"> · {memberPhone}</span>
              </div>
              <div className="text-xs text-sky-700 mt-1 inline-flex items-center gap-1 font-medium">
                Open user details <ExternalLink size={12} />
              </div>
            </button>
          )}

          <div className="rounded-xl border border-orange-100 bg-orange-50/80 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-700/80">Astrologer ID</div>
            <div className="mt-1 font-mono text-sm font-semibold text-gray-900">
              Auto from “{app.name}”
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <span>Email: <strong className="font-medium text-gray-800">{app.email || '—'}</strong></span>
              <span>Phone: <strong className="font-medium text-gray-800">{app.phone}</strong></span>
            </div>
          </div>

          {!app.email && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              This application has no email. Ask the applicant to resubmit with email before approving.
            </p>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Rates (₹ / min)</div>
            <div className="grid grid-cols-3 gap-3">
              <label className="block text-xs text-gray-600">
                Chat
                <input
                  type="number"
                  min={1}
                  className="input mt-1.5"
                  value={approveForm.chatPrice}
                  onChange={(e) => setApproveForm({ ...approveForm, chatPrice: Number(e.target.value) })}
                />
              </label>
              <label className="block text-xs text-gray-600">
                Audio
                <input
                  type="number"
                  min={1}
                  className="input mt-1.5"
                  value={approveForm.callPrice}
                  onChange={(e) => setApproveForm({ ...approveForm, callPrice: Number(e.target.value) })}
                />
              </label>
              <label className="block text-xs text-gray-600">
                Video
                <input
                  type="number"
                  min={1}
                  className="input mt-1.5"
                  value={approveForm.videoPrice}
                  onChange={(e) => setApproveForm({ ...approveForm, videoPrice: Number(e.target.value) })}
                />
              </label>
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Password{' '}
            {app.hasChosenPassword ? (
              <span className="font-normal text-gray-400">(optional — applicant already set one)</span>
            ) : (
              <span className="text-red-500">*</span>
            )}
            <input
              className="input mt-1.5"
              type="text"
              value={approveForm.password}
              onChange={(e) => setApproveForm({ ...approveForm, password: e.target.value })}
              placeholder={
                app.hasChosenPassword
                  ? 'Leave blank to keep applicant password'
                  : 'Min 6 characters — shown once after approve'
              }
              autoComplete="off"
            />
            <span className="mt-1 block text-xs text-gray-400">
              {app.hasChosenPassword
                ? 'Blank = use the password the applicant chose. Or set a new one to override.'
                : 'Share this with the astrologer after approval (SMS/email not connected yet).'}
            </span>
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Note <span className="font-normal text-gray-400">(optional)</span>
            <textarea
              className="textarea mt-1.5"
              rows={2}
              value={approveForm.note}
              onChange={(e) => setApproveForm({ ...approveForm, note: e.target.value })}
              placeholder="Internal note for history…"
            />
          </label>

          <div className="flex gap-2 pt-1 border-t border-gray-100">
            <button type="button" className="btn-secondary flex-1 justify-center" onClick={() => setApproveOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary flex-1 justify-center"
              disabled={
                approveMut.isPending
                || !app.email
                || (
                  !app.hasChosenPassword
                  && (!approveForm.password.trim() || approveForm.password.trim().length < 6)
                )
                || (
                  Boolean(approveForm.password.trim())
                  && approveForm.password.trim().length < 6
                )
              }
              onClick={() => approveMut.mutate()}
            >
              {approveMut.isPending ? 'Approving…' : 'Approve & create'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject application" size="sm">
        <div className="space-y-3">
          <label className="text-sm block">Rejection reason (required)
            <textarea className="textarea mt-1" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Explain what needs to be fixed…" />
          </label>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setRejectOpen(false)}>Cancel</button>
            <button className="btn-danger" disabled={!reason.trim() || rejectMut.isPending} onClick={() => rejectMut.mutate()}>
              {rejectMut.isPending ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!credentials} onClose={() => setCredentials(null)} title="Share login details" size="md">
        {credentials && (
          <div className="space-y-4">
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              SMS and email services are not connected yet. Copy the message and share manually to the applicant’s email/phone.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Email</div>
                <div className="font-medium break-all">{credentials.email || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-400">Phone</div>
                <div className="font-medium">{credentials.phone || '—'}</div>
              </div>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div>
                <div className="text-xs text-gray-400">Astrologer ID (auto)</div>
                <div className="font-mono font-semibold">{credentials.loginId}</div>
              </div>
              <button className="btn-secondary" onClick={() => copyText(credentials.loginId)}><Copy size={14} /></button>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div>
                <div className="text-xs text-gray-400">Password</div>
                <div className="font-mono font-semibold">{credentials.password}</div>
              </div>
              <button className="btn-secondary" onClick={() => copyText(credentials.password)}><Copy size={14} /></button>
            </div>
            {credentials.shareMessage && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs text-gray-500">Ready-to-send message</div>
                  <button className="btn-secondary btn-sm" onClick={() => copyText(credentials.shareMessage!)}>
                    <Copy size={14} /> Copy message
                  </button>
                </div>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap font-sans text-gray-700">
                  {credentials.shareMessage}
                </pre>
              </div>
            )}
            <button className="btn-primary w-full" onClick={() => setCredentials(null)}>Done</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
